import * as React from "react";
import { assetPath } from "@ewn/shared";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deleteArticle,
  listArticles,
  listAssets,
  publishArticle,
  readArticle,
  saveArticleLocale,
  setArticleCover,
  unpublishArticle,
  type AdminArticle,
} from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { useLocaleChoice, useLocales } from "@/lib/useLocales";
import { when } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Select, Textarea } from "@/components/ui/input";
import { ConfirmModal } from "@/components/ui/modal";
import { Notice, Warnings } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Writing one article, in one language at a time.
 *
 * THE SLUG LIVES HERE AND NOWHERE ELSE. Saving a language writes the words, the
 * page's SEO row and -- when the slug moved -- the redirect, all in one batch.
 * The SEO screen refuses an article slug that disagrees with this one, which is
 * what makes "one slug per article per language" a fact rather than a
 * convention two screens are each trying to keep.
 *
 * PUBLISHING IS A PROPERTY OF THE ARTICLE, not of a language. It needs words in
 * at least one, and the worker refuses otherwise; this screen shows which
 * languages exist so that refusal is never a surprise.
 *
 * A DRAFT CAN BE DELETED AND A PUBLISHED ARTICLE CANNOT. Unpublish first --
 * which is a step, deliberately, because deleting an article takes its URL with
 * it and everything linking to that URL stops working.
 */
export function ArticleScreen() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const localeList = useLocales();
  const assetList = useAsync(() => listAssets(), []);
  // The article's own row comes from the LIST rather than from a read of one:
  // `/admin/content/articles/:id/:locale` answers about a translation, and the
  // status, cover and language set are the article's.
  const articleList = useAsync(() => listArticles(), []);

  const article = (articleList.data?.articles ?? []).find((a) => a.id === id) ?? null;
  const locales = localeList.locales;
  // Preferring the language this article already HAS: opening a Spanish-only
  // article on the English tab would show an empty editor beside a row that
  // plainly has words in it.
  const [locale, setLocale] = useLocaleChoice(locales, article?.locales[0]?.locale);

  const action = useAction();
  const [confirm, setConfirm] = React.useState<"publish" | "unpublish" | "delete" | null>(null);

  async function act(fn: () => Promise<unknown>, thenGo?: string) {
    const ok = await action.run(fn);
    setConfirm(null);
    if (ok && thenGo) navigate(thenGo);
    else articleList.reload();
  }

  if (articleList.loading && !article) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="space-y-4">
        <PageHeader title="Article" />
        <Notice tone="error" title="No article with that id">
          It may have been deleted.{" "}
          <Link to="/articles" className="underline underline-offset-4">
            Back to the list
          </Link>
          .
        </Notice>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        pretitle={article.status === "published" ? "Published" : "Draft"}
        title={article.locales[0]?.title ?? "Untitled article"}
        description={`Created ${when(article.createdAt)}`}
      >
        {article.status === "published" ? (
          <Button size="sm" variant="outline" disabled={action.busy} onClick={() => setConfirm("unpublish")}>
            Unpublish
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={action.busy || article.locales.length === 0}
            title={article.locales.length === 0 ? "Write it in at least one language first" : undefined}
            onClick={() => setConfirm("publish")}
          >
            Publish
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={action.busy || article.status === "published"}
          title={article.status === "published" ? "Unpublish it first" : undefined}
          onClick={() => setConfirm("delete")}
        >
          Delete
        </Button>
      </PageHeader>

      {articleList.error && <Notice tone="error">{articleList.error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-4 lg:order-1">
          {locale && (
            <LocaleEditor
              articleId={article.id}
              locale={locale}
              locales={locales.map((l) => ({ code: l.code, name: l.name }))}
              written={article.locales.map((l) => l.locale)}
              onLocale={setLocale}
              onSaved={() => articleList.reload()}
            />
          )}
        </div>

        <div className="space-y-4 lg:order-2">
          <CoverCard
            article={article}
            assetKeys={(assetList.data?.assets ?? []).map((a) => a.key)}
            onSaved={() => articleList.reload()}
          />
          <LanguagesCard article={article} />
        </div>
      </div>

      <ConfirmModal
        open={confirm !== null}
        busy={action.busy}
        onClose={() => setConfirm(null)}
        title={
          confirm === "delete"
            ? "Delete this article?"
            : confirm === "publish"
              ? "Publish this article?"
              : "Take it off the site?"
        }
        confirmLabel={confirm === "delete" ? "Delete it" : confirm === "publish" ? "Publish" : "Unpublish"}
        body={
          confirm === "delete" ? (
            <>
              <p>Every language of it goes, and so does its URL.</p>
              <p className="mt-2">Anything linking to it stops working. This cannot be undone.</p>
            </>
          ) : confirm === "publish" ? (
            <p>
              It goes out with the next build, in {article.locales.length}{" "}
              {article.locales.length === 1 ? "language" : "languages"}.
            </p>
          ) : (
            <p>
              It becomes a draft again and leaves the site at the next build. Nothing written is
              lost.
            </p>
          )
        }
        onConfirm={() => {
          if (confirm === "delete") void act(() => deleteArticle(article.id), "/articles");
          else if (confirm === "publish") void act(() => publishArticle(article.id));
          else void act(() => unpublishArticle(article.id));
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ the words

function LocaleEditor({
  articleId,
  locale,
  locales,
  written,
  onLocale,
  onSaved,
}: {
  articleId: string;
  locale: string;
  locales: { code: string; name: string }[];
  /** Which languages this article already has, so an empty one is not a lie. */
  written: string[];
  onLocale: (code: string) => void;
  onSaved: () => void;
}) {
  const action = useAction();
  const [form, setForm] = React.useState({ slug: "", title: "", excerpt: "", bodyMd: "" });
  const [redirect, setRedirect] = React.useState<string | null>(null);

  const exists = written.includes(locale);

  const { data, loading, error } = useAsync(
    () =>
      exists
        ? readArticle(articleId, locale)
        : // Not an error: a language nobody has written yet is the ordinary
          // state of a new translation, and a 404 shown as a failure would tell
          // the operator something is wrong with the thing they came to create.
          Promise.resolve(null),
    [articleId, locale, exists],
  );

  React.useEffect(() => {
    setRedirect(null);
    setForm({
      slug: data?.slug ?? "",
      title: data?.title ?? "",
      excerpt: data?.excerpt ?? "",
      bodyMd: data?.bodyMd ?? "",
    });
  }, [data, locale]);

  async function save() {
    const ok = await action.run(async () => {
      const result = await saveArticleLocale(articleId, locale, {
        slug: form.slug.trim(),
        title: form.title.trim(),
        excerpt: form.excerpt.trim() || null,
        bodyMd: form.bodyMd,
      });
      // The URL moved and the old one now redirects. Worth saying out loud --
      // it is the part of this save that changes something outside the shop.
      setRedirect(result.redirectAdded);
      return result;
    });
    if (ok) onSaved();
  }

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <Card>
      <CardHeader className="flex-wrap gap-2">
        <CardTitle>{exists ? "Edit" : "Write it in a new language"}</CardTitle>
        {/* Its own width: `fieldBase` is `w-full`, and in a flex header that
            means the whole row -- which put this select on a line of its own
            under the title. See `TranslationsScreen` for the same lesson. */}
        <Select
          size="sm"
          className="w-56"
          aria-label="Language"
          value={locale}
          onChange={(e) => onLocale(e.target.value)}
        >
          {locales.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name} ({l.code}){written.includes(l.code) ? "" : " — empty"}
            </option>
          ))}
        </Select>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Notice tone="error">{error}</Notice>}
        {action.error && <Notice tone="error">{action.error}</Notice>}
        <Warnings warnings={action.warnings} />
        {redirect && (
          <Notice tone="info" title="The old URL now redirects">
            <span className="font-mono text-xs">/{redirect}</span> sends visitors to the new one.
          </Notice>
        )}

        {loading && exists ? (
          <Skeleton className="h-80" />
        ) : (
          <>
            <Field label="Title" htmlFor="art-title">
              <Input id="art-title" value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>

            <Field
              label="URL"
              htmlFor="art-slug"
              hint="Lower-case letters, digits and single dashes. Changing it leaves a redirect."
            >
              <Input
                id="art-slug"
                className="font-mono text-xs"
                value={form.slug}
                onChange={(e) => set("slug", e.target.value)}
              />
            </Field>

            <Field label="Summary" htmlFor="art-excerpt" hint="Shown in the list of articles.">
              <Textarea
                id="art-excerpt"
                rows={2}
                value={form.excerpt}
                onChange={(e) => set("excerpt", e.target.value)}
              />
            </Field>

            <Field label="The article" htmlFor="art-body" hint="Markdown.">
              <Textarea
                id="art-body"
                rows={20}
                className="font-mono text-xs"
                value={form.bodyMd}
                onChange={(e) => set("bodyMd", e.target.value)}
              />
            </Field>

            <div className="flex justify-end">
              <Button disabled={action.busy} onClick={() => void save()}>
                {action.busy ? "Saving…" : exists ? "Save" : "Create this language"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------- the article

function CoverCard({
  article,
  assetKeys,
  onSaved,
}: {
  article: AdminArticle;
  assetKeys: string[];
  onSaved: () => void;
}) {
  const action = useAction();
  const current = article.coverAssetKey;

  async function choose(key: string) {
    if (await action.run(() => setArticleCover(article.id, key || null))) onSaved();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cover image</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {action.error && <Notice tone="error">{action.error}</Notice>}
        {current && (
          <div className="flex h-32 items-center justify-center rounded bg-muted">
            <img
              src={assetPath(current)}
              alt=""
              className="max-h-32 max-w-full object-contain"
            />
          </div>
        )}
        <Select
          aria-label="Cover image"
          disabled={action.busy}
          value={current ?? ""}
          onChange={(e) => void choose(e.target.value)}
        >
          <option value="">No cover</option>
          {/* The stored key is always offered, even if the asset list has not
              arrived -- otherwise opening this would silently clear it. */}
          {(current && !assetKeys.includes(current) ? [current, ...assetKeys] : assetKeys).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">
          Uploaded on the{" "}
          <Link to="/assets" className="underline underline-offset-4">
            Images
          </Link>{" "}
          screen. It is the same picture in every language.
        </p>
      </CardContent>
    </Card>
  );
}

function LanguagesCard({ article }: { article: AdminArticle }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Languages</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {article.locales.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Nothing written yet. It cannot be published until one language has words in it.
          </p>
        )}
        {article.locales.map((l) => (
          <div key={l.locale} className="flex items-baseline justify-between gap-2 text-xs">
            <Badge color="gray">{l.locale}</Badge>
            <span className="min-w-0 flex-1 truncate text-secondary">/{l.slug}</span>
            <span className="whitespace-nowrap text-muted-foreground">{when(l.updatedAt)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
